import React, { useRef, useState } from "react";
import { DownloadIcon } from "../../admin/components/common/Icons";
import type {
  CreateTicketPayload,
  TicketPriority,
} from "../../types/supportTicket";

interface Props {
  onSubmit: (payload: CreateTicketPayload) => Promise<void>;
  loading?: boolean;
}

const TicketForm: React.FC<Props> = ({ onSubmit, loading = false }) => {
  const [subject, setSubject] = useState("");
  const [department, setDepartment] = useState("");
  const [service, setService] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("Medium");
  const [message, setMessage] = useState("");
  const [encrypted, setEncrypted] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => [...prev, ...selectedFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadFile = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");

    link.href = url;
    link.download = file.name;
    link.click();

    URL.revokeObjectURL(url);
  };

  const isImageFile = (file: File) => {
    return file.type.startsWith("image/");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSubmit({
      subject,
      department,
      service,
      priority,
      message,
      encrypted,
      files,
    });
  };

  return (
    <form className="support-ticket-form" onSubmit={handleSubmit}>
      <input
        type="text"
        placeholder="Subject Title"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        required
      />

      <div className="ticket-form-grid">
        <select
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          required
        >
          <option value="">Department</option>
          <option value="Technical">Technical</option>
          <option value="Billing">Billing</option>
          <option value="Sales">Sales</option>
          <option value="General">General</option>
        </select>

        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          required
        >
          <option value="">Service</option>
          <option value="Website">Website</option>
          <option value="Hosting">Hosting</option>
          <option value="Domain">Domain</option>
          <option value="ERP">ERP</option>
          <option value="POS">POS</option>
          <option value="Support">Support</option>
        </select>

        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as TicketPriority)}
          required
        >
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Urgent">Urgent</option>
        </select>
      </div>

      <textarea
        rows={8}
        placeholder="Please write your support ticket in detail"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        required
      />

      <div className="ticket-form-footer">
        <div className="ticket-upload-area">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="ticket-hidden-file"
            onChange={handleFileChange}
            accept=".jpg,.gif,.jpeg,.png,.doc,.xls,.pdf,.txt,.docx,.xlsx,.rar,.zip"
          />

          <button
            type="button"
            className="ticket-upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload File
          </button>

          <small>
            Permitted File Types:
            .jpg,.gif,.jpeg,.png,.doc,.xls,.pdf,.txt,.docx,.xlsx,.rar,.zip
          </small>

          {files.length > 0 && (
            <div className="ticket-file-preview-wrap">
              {files.map((file, index) => {
                const previewUrl = isImageFile(file)
                  ? URL.createObjectURL(file)
                  : "";

                return (
                  <div
                    className="ticket-file-preview"
                    key={`${file.name}-${index}`}
                  >
                    {isImageFile(file) ? (
                      <img src={previewUrl} alt={file.name} />
                    ) : (
                      <div className="ticket-file-icon">
                        <i className="fa fa-file-text-o"></i>
                        <span>{file.name}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="ticket-file-remove"
                      onClick={() => removeFile(index)}
                      title="Remove"
                    >
                      ×
                    </button>

                    <button
                      type="button"
                      className="ticket-file-download"
                      onClick={() => downloadFile(file)}
                      title="Download"
                    >
                      <DownloadIcon />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ticket-submit-area">
          <label className="encrypt-check">
            <input
              type="checkbox"
              checked={encrypted}
              onChange={(e) => setEncrypted(e.target.checked)}
            />
            Encrypt Message
          </label>
          <small>(This message contains sensitive data)</small>

          <button type="submit" className="open-ticket-btn" disabled={loading}>
            {loading ? "Submitting..." : "Open Support Ticket"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default TicketForm;
